// recurrenceCount.ts
//
// CN-2515 follow-up: the "Send All Invoices Now" / "Charge All Now" confirmation
// copy shows the up-front total (per-session price x number of sessions). That
// needs the session count *before* the series is created, so it is computed on
// the client here.
//
// This mirrors the backend's IceCube expansion (RecurrenceGroup#build_schedule):
//   - the until-date is INCLUSIVE of that whole calendar day;
//   - daily / weekly / biweekly step by a fixed number of days from the start;
//   - `monthly` means "the Nth weekday of the month" (e.g. "3rd Tuesday"), with
//     the 5th+ week collapsing to "last weekday" (Date#week_of_month returns -1),
//     so no month is ever skipped.
// The day/week formulas and boundary were verified against the live ice_cube gem.

import { DateTime } from "luxon";
import { RepeatType } from "app/models/EventModel";

type DateInput = Date | DateTime | string | null | undefined;

const toDateTime = (value: DateInput): DateTime | null => {
  if (!value) {return null;}
  if (DateTime.isDateTime(value)) {return value;}
  if (value instanceof Date) {return DateTime.fromJSDate(value);}

  const parsed = DateTime.fromISO(String(value));
  return parsed.isValid ? parsed : null;
};

// Mirrors Date#week_of_month (config/initializers/extensions/date.rb in the
// backend): ceil(dayOfMonth / 7), with anything past the 4th week collapsing to
// -1, which IceCube reads as "the last weekday of the month".
const weekOfMonth = (day: number): number => {
  const week = Math.ceil(day / 7);
  return week > 4 ? -1 : week;
};

// Date of the Nth (or last, when n === -1) `weekday` of the given month.
// `weekday` uses luxon's convention: 1 = Monday ... 7 = Sunday.
const nthWeekdayOfMonth = (
  year: number,
  month: number,
  weekday: number,
  n: number,
): DateTime => {
  if (n === -1) {
    let cursor = DateTime.local(year, month, 1).endOf("month").startOf("day");
    while (cursor.weekday !== weekday) {
      cursor = cursor.minus({ days: 1 });
    }
    return cursor;
  }

  const firstOfMonth = DateTime.local(year, month, 1);
  const offset = (weekday - firstOfMonth.weekday + 7) % 7;
  return firstOfMonth.plus({ days: offset }).plus({ weeks: n - 1 });
};

// Safety net only — the event form caps series length well below this.
const MAX_MONTHLY_ITERATIONS = 200;

// Number of sessions a repeating series creates between `start` and `untilDate`
// (inclusive), matching the backend recurrence expansion. Falls back to 1 for a
// non-repeating or unparseable input.
export const sessionCountForRecurrence = (
  repeat: string | null | undefined,
  start: DateInput,
  untilDate: DateInput,
): number => {
  const startDt = toDateTime(start)?.startOf("day");
  const untilDt = toDateTime(untilDate)?.startOf("day");

  if (!repeat || !startDt) {return 1;}
  if (!untilDt || untilDt < startDt) {return 1;}

  const days = Math.floor(untilDt.diff(startDt, "days").days);

  switch (repeat) {
    case RepeatType.DAILY:
      return days + 1;
    case RepeatType.WEEKLY:
      return Math.floor(days / 7) + 1;
    case RepeatType.BIWEEKLY:
      return Math.floor(days / 14) + 1;
    case RepeatType.MONTHLY: {
      const weekday = startDt.weekday;
      const n = weekOfMonth(startDt.day);
      let count = 0;
      let cursor = startDt.startOf("month");

      for (let i = 0; i < MAX_MONTHLY_ITERATIONS; i += 1) {
        const occurrence = nthWeekdayOfMonth(cursor.year, cursor.month, weekday, n);
        if (occurrence > untilDt) {break;}
        if (occurrence >= startDt) {count += 1;}
        cursor = cursor.plus({ months: 1 });
      }

      return count;
    }
    default:
      return 1;
  }
};
